import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const EMPTY_PLACEMENT = {
  label: '',
  key: '',
  library_key: '',
  description: '',
};

const FALLBACK_PLACEMENTS = [
  { key: 'invoice.print_pdf', label: 'Invoice Print/PDF', library_key: 'invoice', description: 'Invoice document output selector', is_enabled: true, is_system: true },
  { key: 'quote.print_pdf', label: 'Quote Print/PDF', library_key: 'quote', description: 'Operational quote document output selector', is_enabled: true, is_system: true },
  { key: 'prospect_quote.print_pdf', label: 'Prospect Quote Print/PDF', library_key: 'prospect_quote', description: 'Prospect quote document output selector', is_enabled: true, is_system: true },
  { key: 'ppm_quote.print_pdf', label: 'PPM Quote Print/PDF', library_key: 'ppm_quote', description: 'PPM quote document output selector', is_enabled: true, is_system: true },
  { key: 'ppm_contract.print_pdf', label: 'PPM Contract Print/PDF', library_key: 'ppm_contract', description: 'PPM contract document output selector', is_enabled: true, is_system: true },
  { key: 'job.print_pdf', label: 'Job Print/PDF', library_key: 'job_sheet', description: 'Job sheet document output selector', is_enabled: true, is_system: true },
  { key: 'sales_order.print_pdf', label: 'Sales Order Print/PDF', library_key: 'sales_order', description: 'Sales order document output selector', is_enabled: true, is_system: true },
  { key: 'purchase_order.print_pdf', label: 'Purchase Order Print/PDF', library_key: 'purchase_order', description: 'Purchase order document output selector', is_enabled: true, is_system: true },
  { key: 'delivery_note.print_pdf', label: 'Delivery Note Print/PDF', library_key: 'delivery_note', description: 'Delivery note document output selector', is_enabled: true, is_system: true },
  { key: 'risk_assessment.print_pdf', label: 'Risk Assessment Print/PDF', library_key: 'risk_assessment', description: 'Risk assessment document output selector', is_enabled: true, is_system: true },
  { key: 'service_report.print_pdf', label: 'Service Report Print/PDF', library_key: 'service_report', description: 'Service report document output selector', is_enabled: true, is_system: true },
  { key: 'completion_certificate.print_pdf', label: 'Completion Certificate Print/PDF', library_key: 'completion_certificate', description: 'Completion certificate document output selector', is_enabled: true, is_system: true },
  { key: 'compliance_forms.print_pdf', label: 'Compliance Forms Print/PDF', library_key: 'compliance_forms', description: 'Compliance form document output selector', is_enabled: true, is_system: true },
];

function libraryLabel(libraries, key) {
  return libraries.find((library) => library.key === key)?.label || key || 'Not assigned';
}

function DocumentTemplatePlacementsSetup() {
  const [libraries, setLibraries] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [newPlacement, setNewPlacement] = useState(EMPTY_PLACEMENT);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const enabledLibraries = useMemo(
    () => libraries.filter((library) => library.is_enabled !== false),
    [libraries]
  );

  const unallocatedLibraries = useMemo(() => {
    const allocatedKeys = new Set(placements.map((placement) => placement.library_key).filter(Boolean));
    return enabledLibraries.filter((library) => !allocatedKeys.has(library.key));
  }, [enabledLibraries, placements]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [librariesRes, placementsRes] = await Promise.all([
        apiFetch('/api/documents/libraries'),
        apiFetch('/api/documents/placements'),
      ]);
      const librariesData = await librariesRes.json();
      const placementsData = await placementsRes.json();
      setLibraries(Array.isArray(librariesData) ? librariesData : []);
      setPlacements(Array.isArray(placementsData) ? placementsData : FALLBACK_PLACEMENTS);
      const firstLibrary = Array.isArray(librariesData) && librariesData.length > 0 ? librariesData[0].key : '';
      setNewPlacement((prev) => ({ ...prev, library_key: prev.library_key || firstLibrary }));
    } catch (err) {
      setPlacements(FALLBACK_PLACEMENTS);
      setError(`${err.message || 'Failed to load document placements.'} Showing default placements until the backend responds.`);
    } finally {
      setLoading(false);
    }
  }

  async function updatePlacement(placement, patch) {
    setSavingKey(placement.key);
    setError(null);
    try {
      const res = await apiFetch(`/api/documents/placements/${placement.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update placement.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update placement.');
    } finally {
      setSavingKey('');
    }
  }

  async function createPlacement(event) {
    event.preventDefault();
    setSavingKey('new');
    setError(null);
    try {
      const res = await apiFetch('/api/documents/placements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPlacement),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create placement.');
      setNewPlacement({ ...EMPTY_PLACEMENT, library_key: enabledLibraries[0]?.key || '' });
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create placement.');
    } finally {
      setSavingKey('');
    }
  }

  async function deletePlacement(placement) {
    if (placement.is_system) {
      setError('System placements cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete the ${placement.label} placement?`)) return;

    setSavingKey(placement.key);
    setError(null);
    try {
      const res = await apiFetch(`/api/documents/placements/${placement.key}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete placement.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete placement.');
    } finally {
      setSavingKey('');
    }
  }

  function startPlacementForLibrary(library) {
    setNewPlacement({
      label: `${library.label} Print/PDF`,
      key: `${library.key}.print_pdf`,
      library_key: library.key,
      description: `${library.label} document output selector`,
    });
    setError(null);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Document Placement Setup"
        subtitle="Assign document template libraries to module dropdowns"
        backTo="/settings/operations-files"
        backLabel="Operations Setup"
        sticky
      />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-4">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-semibold">Close</button>
          </div>
        )}

        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Module Dropdowns</h2>
              <p className="text-sm text-gray-500 mt-1">
                Each placement controls which template library appears in that module's document selector.
              </p>
            </div>
            <button
              type="button"
              onClick={loadData}
              disabled={loading || !!savingKey}
              className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-sm text-gray-700"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading placements...</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {placements.map((placement) => (
                <div key={placement.key} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-11 h-11 rounded-lg bg-[#082547] text-white flex items-center justify-center text-xs font-bold">
                        {placement.library_marker || 'DOC'}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 truncate">{placement.label}</h3>
                          {placement.is_system && <span className="text-xs text-gray-400">system</span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{placement.description || 'Document selector placement'}</p>
                        <p className="text-xs text-gray-400 mt-1">{placement.key}</p>
                      </div>
                    </div>
                    <label className="block text-xs font-medium text-gray-500 mt-4 mb-1">Assigned library</label>
                    <select
                      value={placement.library_key || ''}
                      onChange={(event) => updatePlacement(placement, { library_key: event.target.value })}
                      disabled={savingKey === placement.key || enabledLibraries.length === 0 || !placement.id}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20 disabled:bg-gray-50"
                    >
                      {enabledLibraries.map((library) => (
                        <option key={library.key} value={library.key}>{library.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Currently: {libraryLabel(libraries, placement.library_key)}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => updatePlacement(placement, { is_enabled: placement.is_enabled === false })}
                      disabled={savingKey === placement.key || !placement.id}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
                        placement.is_enabled !== false ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {placement.is_enabled !== false ? 'Enabled' : 'Disabled'}
                    </button>
                    <button
                      type="button"
                      onClick={() => deletePlacement(placement)}
                      disabled={savingKey === placement.key || placement.is_system || !placement.id}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {!loading && unallocatedLibraries.length > 0 && (
          <section className="bg-white rounded-lg border border-orange-200 p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Unallocated Libraries</h2>
              <p className="text-sm text-gray-500 mt-1">
                These enabled libraries exist in setup but are not currently assigned to any placement.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {unallocatedLibraries.map((library) => (
                <div key={library.key} className="border border-orange-100 bg-orange-50 rounded-lg p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-9 rounded-lg bg-[#082547] text-white flex items-center justify-center text-xs font-bold">
                        {library.marker || 'DOC'}
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{library.label}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{library.key}</p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => startPlacementForLibrary(library)}
                    disabled={!!savingKey}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-orange-700 border border-orange-200 hover:bg-orange-100 disabled:opacity-50"
                  >
                    Prepare placement
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Dropdown Placement</h2>
          <form onSubmit={createPlacement} className="grid md:grid-cols-4 gap-3">
            <input
              type="text"
              value={newPlacement.label}
              onChange={(event) => setNewPlacement((prev) => ({ ...prev, label: event.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="Placement name"
              disabled={!!savingKey || enabledLibraries.length === 0}
              required
            />
            <input
              type="text"
              value={newPlacement.key}
              onChange={(event) => setNewPlacement((prev) => ({ ...prev, key: event.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="placement.key"
              disabled={!!savingKey || enabledLibraries.length === 0}
            />
            <select
              value={newPlacement.library_key}
              onChange={(event) => setNewPlacement((prev) => ({ ...prev, library_key: event.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              disabled={!!savingKey || enabledLibraries.length === 0}
              required
            >
              {enabledLibraries.map((library) => (
                <option key={library.key} value={library.key}>{library.label}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!!savingKey || enabledLibraries.length === 0}
              className="bg-[#082547] hover:bg-[#0a2d5c] disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Add Placement
            </button>
            <input
              type="text"
              value={newPlacement.description}
              onChange={(event) => setNewPlacement((prev) => ({ ...prev, description: event.target.value }))}
              className="md:col-span-4 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
              placeholder="Description"
              disabled={!!savingKey || enabledLibraries.length === 0}
            />
          </form>
        </section>
      </main>
    </div>
  );
}

export default DocumentTemplatePlacementsSetup;
